import { useState } from "react";
import { Link, useParams } from "wouter";
import { useGetAvatar } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion, AnimatePresence } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import { ChevronLeft, ChevronRight, ShoppingCart, Zap, ExternalLink } from "lucide-react";

const RARITY_COLORS: Record<string, string> = {
  common: "text-gray-400 border-gray-400/40 bg-gray-400/10",
  rare: "text-blue-400 border-blue-400/40 bg-blue-400/10",
  epic: "text-purple-400 border-purple-400/40 bg-purple-400/10",
  legendary: "text-yellow-400 border-yellow-400/40 bg-yellow-400/10",
};

const RARITY_GLOW: Record<string, string> = {
  common: "shadow-gray-400/20",
  rare: "shadow-blue-400/30",
  epic: "shadow-purple-500/40",
  legendary: "shadow-yellow-400/50",
};

const AVATAR_GALLERIES: Record<number, string[]> = {
  1: [
    "/emerald-onyx.webp",
    "/emerald-onyx-2.webp",
    "/emerald-onyx-3.webp",
    "/emerald-onyx-4.webp",
  ],
  2: [
    "/misty-haze.webp",
    "/misty-haze-2.webp",
    "/misty-haze-3.webp",
    "/misty-haze-4.webp",
  ],
  3: [
    "/queen-irry.png",
    "/queen-irry-2.jpeg",
    "/queen-irry-3.webp",
    "/queen-irry-4.jpeg",
  ],
  4: [
    "/goldie.webp",
    "/goldie-2.jpeg",
    "/goldie-3.webp",
    "/goldie-4.jpeg",
  ],
  5: [
    "/swan-queen-1.jpeg",
    "/swan-queen-2.png",
    "/swan-queen-3.png",
    "/swan-queen-4.png",
  ],
  6: [
    "/void-walker-main.webp",
    "/void-walker-sheet1.webp",
    "/void-walker-sheet2.webp",
  ],
  7: [
    "/arie-portrait.jpg",
    "/arie-pose1.jpg",
    "/arie-pose2.jpg",
    "/arie-pose3.jpg",
  ],
  9: [
    "/werewolf-main.webp",
    "/werewolf-sheet.webp",
    "/werewolf-scene.jpeg",
  ],
  10: [
    "/claudia-g-cover.webp",
    "/claudia-g-model-sheet.webp",
    "/claudia-g-runway.webp",
    "/claudia-g-beach.webp",
    "/claudia-g-selfie.jpeg",
    "/claudia-g-studio.webp",
    "/claudia-g-studio2.jpeg",
  ],
  8: [
    "/mst-leopard.png",
    "/mst-runway.png",
    "/mst-back.png",
    "/mst-mask.png",
  ],
};

const AVATAR_BIOS: Record<number, string> = {
  1: `Emerald Onyx is one of the ISM Universe's most electrifying combatants — a legendary warrior clad in a signature yellow-and-green superhero suit that radiates raw power. Known for dominating arenas and commanding every room she enters, Emerald Onyx is the ISM definition of unstoppable.`,
  2: `Misty-Haze moves through the ISM Universe like smoke — impossible to catch, unforgettable to witness. Behind the mask lies an enigma: part streetwear icon, part underground legend. Whether she's headlining events or vanishing into the metaverse mist, Misty-Haze keeps the crowd guessing.`,
  3: `Queen Irry rules the ISM Universe with an otherworldly grace that no other avatar can match. Born of lush green energy and regal bearing, she commands loyalty from every corner of the metaverse. Her legendary status isn't just earned — it's written into the code of New World itself.`,
  4: `Goldie arrives in every world draped in pure luxury. With her signature gold silhouette and effortless glamour, she turns every venue into a fashion moment. An epic-tier presence who proves that in the ISM Universe, style is the ultimate currency.`,
  5: `Swan Queen — Ixchel-Who-Walks — is the celestial guardian of the Angels of ISM | Ref's of ILL saga. Part ancient spirit, part apex warrior, she moves through the ISM Universe draped in sacred white swan wings and glowing turquoise sun sigils. Wielding her spear of light and adorned with a warrior's headdress, she is both protector and sovereign — a legendary presence that bridges worlds between the mortal realm and the divine metaverse.`,
  6: `Void Walker — known in the Blood Eclipse codex as Shadow Smokey — is a being without origin and without end. A living silhouette of dark energy and trailing smoke, he moves through the ISM Universe undetected and unstoppable. No scan can track him, no world can hold him. Equal parts myth and menace, Void Walker is the ISM Universe's most feared and elusive entity.`,
  7: `Arie is one of the ISM Universe's most sought-after presences — an IMVU Model, Host, and Designer who brings elegance and edge to every space she enters. With her signature leopard looks and dark editorial style, Arie defines what it means to build a digital brand from the ground up.`,
  9: `Werewolf (Beta) is the first born of the Blood Eclipse series — a massive, black-furred apex predator standing at the crossroads of human and wolf. Still in Beta form, his power is raw and barely contained. Claws forged from moonlight, eyes that cut through every shadow, and a presence that sends tremors through every world he enters. The Blood Eclipse saga has just begun.`,
  10: `The Time Travel Agent — Beach Trip v. 1 is Claudia G.'s iconic ISM Universe debut: sun-kissed, effortlessly powerful, and impossible to ignore. Draped in a signature shell-chain bikini and flower crown with those unmistakable blue mirrored shades, Claudia arrives from another timeline altogether. Model, muse, and comic series protagonist — she stars in the Behind the Scenes series and her own comic saga. Owning this avatar means carrying a piece of her story into every world you enter.`,
  8: `Ms. T "Blood Moon Queen" is a force of nature in the ISM Universe — commanding, mysterious, and iconic. Known for her signature masquerade mask and leopard-print aesthetic, she moves through every world leaving a trail of awe. Whether ruling the runway or hosting exclusive events, Ms. T is the standard.`,
};

const AVATAR_LINKS: Record<number, { label: string; url: string }> = {
  10: { label: "ClaudiaGLifestyle.com", url: "https://ClaudiaGLifestyle.com/lifestyle" },
};

export default function AvatarDetail() {
  const params = useParams<{ id: string }>();
  const id = parseInt(params.id ?? "0", 10);
  const { data: avatar, isLoading, isError } = useGetAvatar(id);
  const { toast } = useToast();
  const [activeImg, setActiveImg] = useState(0);

  const gallery = AVATAR_GALLERIES[id] ?? (avatar?.previewUrl ? [avatar.previewUrl] : []);

  const handlePrev = () => setActiveImg((i) => (i === 0 ? gallery.length - 1 : i - 1));
  const handleNext = () => setActiveImg((i) => (i === gallery.length - 1 ? 0 : i + 1));

  const handlePurchase = () => {
    toast({
      title: "Purchase Initiated",
      description: `Connecting wallet to mint ${avatar?.name ?? "avatar"} on ${avatar?.blockchain ?? "blockchain"}…`,
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (isError || !avatar) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-center px-4">
        <p className="text-2xl font-bold text-muted-foreground">Avatar not found</p>
        <Link href="/avatars">
          <Button variant="outline">Back to Avatars</Button>
        </Link>
      </div>
    );
  }

  const rarityClass = RARITY_COLORS[avatar.rarity ?? "common"];
  const glowClass = RARITY_GLOW[avatar.rarity ?? "common"];
  const bio = AVATAR_BIOS[id] ?? `A ${avatar.rarity} ${avatar.style} avatar available in the ISM Universe. Own this identity on-chain and make it yours across every world.`;
  const externalLink = AVATAR_LINKS[id];

  return (
    <div className="min-h-screen bg-background text-foreground pb-24">
      <div className="container mx-auto px-4 pt-8 max-w-6xl">
        <Link href="/avatars">
          <button className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8 group">
            <ChevronLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Back to Avatars
          </button>
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Gallery */}
          <div className="flex flex-col gap-4">
            <div className={`relative aspect-[3/4] rounded-2xl overflow-hidden bg-card/30 border border-border shadow-2xl ${glowClass}`}>
              <AnimatePresence mode="wait">
                <motion.img
                  key={activeImg}
                  src={gallery[activeImg]}
                  alt={`${avatar.name} look ${activeImg + 1}`}
                  className="w-full h-full object-cover object-top"
                  initial={{ opacity: 0, scale: 1.04 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.97 }}
                  transition={{ duration: 0.35 }}
                />
              </AnimatePresence>

              {gallery.length > 1 && (
                <>
                  <button
                    onClick={handlePrev}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 backdrop-blur-md border border-white/10 flex items-center justify-center hover:bg-black/80 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleNext}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 backdrop-blur-md border border-white/10 flex items-center justify-center hover:bg-black/80 transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {gallery.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setActiveImg(i)}
                        className={`w-2 h-2 rounded-full transition-all ${i === activeImg ? "bg-white scale-125" : "bg-white/30 hover:bg-white/60"}`}
                      />
                    ))}
                  </div>
                </>
              )}

              {avatar.rarity && (
                <div className={`absolute top-4 left-4 px-3 py-1 rounded text-xs uppercase font-bold tracking-wider border ${rarityClass}`}>
                  {avatar.rarity}
                </div>
              )}
            </div>

            {/* Thumbnail strip */}
            {gallery.length > 1 && (
              <div className="grid grid-cols-4 gap-2">
                {gallery.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${i === activeImg ? "border-primary" : "border-border opacity-60 hover:opacity-90"}`}
                  >
                    <img src={img} alt={`Look ${i + 1}`} className="w-full h-full object-cover object-top" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info Panel */}
          <motion.div
            className="flex flex-col gap-6"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <div>
              <p className="text-sm text-primary uppercase tracking-widest font-mono mb-2">{avatar.style} / {avatar.blockchain}</p>
              <h1 className="text-4xl md:text-5xl font-bold uppercase tracking-tight leading-tight mb-4">{avatar.name}</h1>
              <p className="text-muted-foreground text-base leading-relaxed">{bio}</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {[
                { label: "Rarity", value: avatar.rarity ?? "—" },
                { label: "Style", value: avatar.style },
                { label: "Blockchain", value: avatar.blockchain },
                { label: "Token ID", value: avatar.tokenId ?? "—" },
              ].map(({ label, value }) => (
                <div key={label} className="bg-card/40 border border-border rounded-xl p-4">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{label}</p>
                  <p className="font-mono font-bold text-sm truncate">{value}</p>
                </div>
              ))}
            </div>

            <div className="bg-card/40 border border-border rounded-2xl p-6">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Price</p>
              <div className="flex items-baseline gap-2 mb-6">
                <span className="text-5xl font-bold font-mono">{avatar.price?.toLocaleString()}</span>
                <span className="text-xl text-primary font-mono font-bold">{avatar.currency}</span>
              </div>
              {avatar.owned ? (
                <Button className="w-full bg-primary hover:bg-primary/80 text-primary-foreground font-bold tracking-widest uppercase h-14 text-base" size="lg">
                  <Zap className="w-5 h-5 mr-2" />
                  Equip Avatar
                </Button>
              ) : (
                <div className="flex flex-col gap-3">
                  <Button
                    onClick={handlePurchase}
                    className="w-full bg-white text-black hover:bg-white/90 font-bold tracking-widest uppercase h-14 text-base"
                    size="lg"
                  >
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    Purchase Now
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full border-border hover:bg-card font-bold tracking-widest uppercase h-12"
                  >
                    Add to Wishlist
                  </Button>
                </div>
              )}
            </div>

            {externalLink && (
              <a
                href={externalLink.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Button
                  variant="outline"
                  className="w-full border-primary/50 text-primary hover:bg-primary/10 hover:border-primary font-bold tracking-widest uppercase h-12 gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  Visit {externalLink.label}
                </Button>
              </a>
            )}

            <div className="text-xs text-muted-foreground text-center">
              Ownership is recorded on-chain. Token transfer and resale governed by ISM Universe terms.
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
